import { useState } from "react";
import axios from "axios";
import { KeyRound, Copy, Check, Loader2, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PasswordResetAdmin() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    setResetLink("");
    try {
      const res = await axios.post(`${API}/admin/password-reset`, { email: email.trim() });
      setResetLink(`${window.location.origin}/reset-password?token=${res.data.token}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al generar link");
    }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(resetLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={16} className="text-emerald-400" />
        <h3 className="text-sm font-semibold text-zinc-300 tracking-wide uppercase" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Resetear Contrasena de Alumno
        </h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Genera un link para que el alumno pueda cambiar su contrasena. Envialo por WhatsApp.
      </p>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 mb-3">
          <AlertCircle size={12} className="text-red-400" />
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <input
          data-testid="reset-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email del alumno"
          className="flex-1 bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors"
        />
        <button
          data-testid="generate-reset-btn"
          onClick={handleGenerate}
          disabled={loading || !email.trim()}
          className="flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-5 py-2.5 text-sm transition-all hover:bg-emerald-400 disabled:opacity-40 shadow-[0_0_20px_rgba(16,185,129,0.2)] whitespace-nowrap"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
          Generar Link
        </button>
      </div>

      <AnimatePresence>
        {resetLink && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-zinc-950/60 border border-emerald-500/20 rounded-xl p-4"
          >
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Link generado (expira en 24h)</p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-emerald-400 font-mono truncate flex-1">{resetLink}</p>
              <button
                data-testid="copy-reset-link-btn"
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-medium transition-all whitespace-nowrap"
              >
                {copied ? <><Check size={12} className="text-emerald-400" /><span className="text-emerald-400">Copiado</span></> : <><Copy size={12} />Copiar</>}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
